import React, { useEffect, useState } from "react";
import { useParams, useNavigate } from "react-router-dom";
import { Order, Workshop, User, OrderStatus } from "../types";
import {
  orderService,
  workshopService,
  userService,
} from "../services/dataService";
import { costService } from "../services/costService";
import { formatCurrency, formatNumber, getImageUrl } from "../utils/formatters";
import WorkshopTransferModal from "../components/WorkshopTransferModal";
import AlertModal from "../components/AlertModal";
import Header from "../components/Header";
import Sidebar from "../components/Sidebar";
import { useAuth } from "../context/AuthContext";
import "./OrderQRDetail.css";

const OrderQRDetail: React.FC = () => {
  const { orderId } = useParams<{ orderId: string }>();
  const navigate = useNavigate();
  const { user } = useAuth();
  const [order, setOrder] = useState<Order | null>(null);
  const [loading, setLoading] = useState(true);
  const [workshops, setWorkshops] = useState<Workshop[]>([]);
  const [users, setUsers] = useState<User[]>([]);
  const [showTransferModal, setShowTransferModal] = useState(false);
  const [selectedWorkshopId, setSelectedWorkshopId] = useState<string>("");
  const [selectedUserId, setSelectedUserId] = useState<string>("");
  const [lightboxImage, setLightboxImage] = useState<string | null>(null);
  const [sidebarOpen, setSidebarOpen] = useState(false);
  const [isEditingNote, setIsEditingNote] = useState(false);
  const [noteText, setNoteText] = useState("");
  const [savingNote, setSavingNote] = useState(false);
  const [isEditingQuantity, setIsEditingQuantity] = useState(false);
  const [quantityValue, setQuantityValue] = useState(0);
  const [savingQuantity, setSavingQuantity] = useState(false);
  const [alertModal, setAlertModal] = useState<{
    isOpen: boolean;
    message: string;
    type: "info" | "success" | "warning" | "error";
  }>({ isOpen: false, message: "", type: "info" });

  useEffect(() => {
    if (orderId) {
      loadOrderDetails();
      loadUsers();
    }
  }, [orderId]);

  // Order yüklendiğinde workshops'u yükle
  useEffect(() => {
    if (order) {
      loadWorkshops();
    }
  }, [order?.workshopId]);

  const loadOrderDetails = async () => {
    try {
      setLoading(true);
      const orderData = await orderService.getById(orderId!);
      setOrder(orderData);
      setNoteText(orderData.note || "");
      setQuantityValue(orderData.quantity || 0);
    } catch (error) {
      console.error("Failed to load order:", error);
    } finally {
      setLoading(false);
    }
  };

  const handleSaveNote = async () => {
    if (!order) return;

    try {
      setSavingNote(true);
      await orderService.update(orderId!, { ...order, note: noteText });
      setOrder({ ...order, note: noteText });
      setIsEditingNote(false);
      setAlertModal({
        isOpen: true,
        message: "Not başarıyla kaydedildi!",
        type: "success",
      });
    } catch (error) {
      console.error("Failed to save note:", error);
      setAlertModal({
        isOpen: true,
        message: "Not kaydedilemedi!",
        type: "error",
      });
    } finally {
      setSavingNote(false);
    }
  };

  const handleCancelNote = () => {
    setNoteText(order?.note || "");
    setIsEditingNote(false);
  };

  const handleSaveQuantity = async () => {
    if (!order) return;

    if (quantityValue <= 0) {
      setAlertModal({
        isOpen: true,
        message: "Adet 0'dan büyük olmalıdır!",
        type: "warning",
      });
      return;
    }

    try {
      setSavingQuantity(true);
      await orderService.update(orderId!, {
        ...order,
        quantity: quantityValue,
      });
      setOrder({ ...order, quantity: quantityValue });
      setIsEditingQuantity(false);
      setAlertModal({
        isOpen: true,
        message: "Adet başarıyla güncellendi!",
        type: "success",
      });
    } catch (error) {
      console.error("Failed to save quantity:", error);
      setAlertModal({
        isOpen: true,
        message: "Adet güncellenemedi!",
        type: "error",
      });
    } finally {
      setSavingQuantity(false);
    }
  };

  const handleCancelQuantity = () => {
    setQuantityValue(order?.quantity || 0);
    setIsEditingQuantity(false);
  };

  const loadWorkshops = async () => {
    try {
      // Eğer sipariş bir atölyeye atanmışsa, sadece o atölyeden transfer yapılabilecek atölyeleri getir
      if (order?.workshopId) {
        const connections = await workshopService.getConnections(
          order.workshopId,
        );
        // Connections'dan workshop listesini çıkar ve sırala
        const connectedWorkshops = connections
          .map((conn: any) => conn.targetWorkshop)
          .sort((a: any, b: any) => {
            const orderA = a.displayOrder ?? 999;
            const orderB = b.displayOrder ?? 999;
            if (orderA !== orderB) return orderA - orderB;
            return a.name.localeCompare(b.name, "tr");
          });
        setWorkshops(connectedWorkshops);
      } else {
        // Atölye atanmamışsa tüm atölyeleri listele
        const data = await workshopService.getAll();
        const sortedData = [...data].sort((a, b) => {
          const orderA = a.displayOrder ?? 999;
          const orderB = b.displayOrder ?? 999;
          if (orderA !== orderB) return orderA - orderB;
          return a.name.localeCompare(b.name, "tr");
        });
        setWorkshops(sortedData);
      }
    } catch (error) {
      console.error("Failed to load workshops:", error);
      // Hata durumunda tüm atölyeleri yükle
      try {
        const data = await workshopService.getAll();
        const sortedData = [...data].sort((a, b) => {
          const orderA = a.displayOrder ?? 999;
          const orderB = b.displayOrder ?? 999;
          if (orderA !== orderB) return orderA - orderB;
          return a.name.localeCompare(b.name, "tr");
        });
        setWorkshops(sortedData);
      } catch (err) {
        console.error("Failed to load all workshops:", err);
      }
    }
  };

  const loadUsers = async () => {
    try {
      const data = await userService.getAll();
      setUsers(data);
    } catch (error) {
      console.error("Failed to load users:", error);
    }
  };

  const formatDate = (dateString?: string) => {
    if (!dateString) return "-";
    const date = new Date(dateString);
    if (isNaN(date.getTime())) return "-";
    return new Intl.DateTimeFormat("tr-TR", {
      year: "numeric",
      month: "long",
      day: "numeric",
      hour: "2-digit",
      minute: "2-digit",
    }).format(date);
  };

  const getStatusColor = (status?: OrderStatus) => {
    if (status === undefined) return "#95a5a6";
    const colors: { [key: number]: string } = {
      [OrderStatus.Atanmadi]: "#f39c12",
      [OrderStatus.Islemde]: "#3498db",
      [OrderStatus.Tamamlandi]: "#27ae60",
      [OrderStatus.IptalEdildi]: "#e74c3c",
    };
    return colors[status] || "#95a5a6";
  };

  const getStatusLabel = (status?: OrderStatus) => {
    if (status === undefined) return "Bilinmiyor";
    const labels: { [key: number]: string } = {
      [OrderStatus.Atanmadi]: "Atanmadı",
      [OrderStatus.Islemde]: "İşlemde",
      [OrderStatus.Tamamlandi]: "Tamamlandı",
      [OrderStatus.IptalEdildi]: "İptal Edildi",
    };
    return labels[status] || "Bilinmiyor";
  };

  const getUnitLabel = (unit?: number) => {
    const units: { [key: number]: string } = {
      1: "Adet",
      2: "Metre",
      3: "Takım",
      7: "Pano",
    };
    return units[unit || 0] || "Adet";
  };

  const handleTransferSave = async (costs: any[]) => {
    if (!order) return;
    try {
      // Seçilen atölyeyi bul
      const targetWorkshop = workshops.find(
        (w) => w.workshopId === selectedWorkshopId,
      );
      const workshopName = targetWorkshop?.name?.toLowerCase() || "";

      // Statüyü atölyeye göre belirle
      let newStatus = order.status;
      if (
        workshopName.includes("biten") ||
        workshopName.includes("tamamlanan") ||
        workshopName.includes("tamamlandı")
      ) {
        // "Tamamlanan İşler/Siparişler" veya "Biten" atölyesine taşınırsa -> Tamamlandı
        newStatus = OrderStatus.Tamamlandi;
      } else if (workshopName.includes("atanmamış")) {
        // "Atanmamış İşler" atölyesine taşınırsa -> Atanmadı
        newStatus = OrderStatus.Atanmadi;
      } else if (selectedWorkshopId) {
        // Diğer atölyelere taşınırsa -> İşlemde
        newStatus = OrderStatus.Islemde;
      }

      // Yeni atama endpointi için payload (artık operatorId gönderme)
      const assignPayload = {
        workshopId: selectedWorkshopId,
        orderStatusId: newStatus,
      };
      await orderService.assign(orderId!, assignPayload);
      // Save model costs if any
      if (costs && costs.length > 0) {
        for (let i = 0; i < costs.length; i++) {
          const cost = costs[i];
          const modelCostData = {
            modelId: order.modelId,
            orderId: order.orderId,
            costItemId: cost.costItemId,
            quantity: cost.quantityUsed,
            unit: cost.unit,
            quantity2: cost.quantity2,
            unit2: cost.unit2,
            quantity3: cost.quantity3,
            unit3: cost.unit3,
            costUnitId3: cost.costUnitId3,
            unitPrice: cost.actualPrice,
            totalCost: cost.totalCost,
            currency: cost.currency,
            usage: order.workshop?.name || undefined,
            isActive: true,
          };
          await costService.addModelCost(modelCostData);
        }
      }

      // Close modal and refresh
      setShowTransferModal(false);
      setSelectedWorkshopId("");
      setSelectedUserId("");
      await loadOrderDetails();

      setAlertModal({
        isOpen: true,
        message: "Atölye transferi başarıyla tamamlandı!",
        type: "success",
      });
    } catch (error) {
      console.error("❌ Transfer failed:", error);
      setAlertModal({
        isOpen: true,
        message: "Transfer sırasında bir hata oluştu!",
        type: "error",
      });
      throw error;
    }
  };

  if (loading) {
    return (
      <>
        <Header onMenuClick={() => setSidebarOpen(!sidebarOpen)} />
        <Sidebar isOpen={sidebarOpen} onClose={() => setSidebarOpen(false)} />
        <div className="qr-detail-container">
          <div className="qr-detail-loading">
            <div className="spinner"></div>
            <p>Sipariş bilgileri yükleniyor...</p>
          </div>
        </div>
      </>
    );
  }

  if (!order) {
    return (
      <>
        <Header onMenuClick={() => setSidebarOpen(!sidebarOpen)} />
        <Sidebar isOpen={sidebarOpen} onClose={() => setSidebarOpen(false)} />
        <div className="qr-detail-container">
          <div className="qr-detail-error">
            <div className="error-icon">⚠️</div>
            <h2>Sipariş Bulunamadı</h2>
            <p>Bu sipariş silinmiş veya mevcut değil.</p>
          </div>
        </div>
      </>
    );
  }

  return (
    <>
      <Header onMenuClick={() => setSidebarOpen(!sidebarOpen)} />
      <Sidebar isOpen={sidebarOpen} onClose={() => setSidebarOpen(false)} />
      <div className="qr-detail-container">
        {/* Header Section */}
        <div className="qr-detail-header">
          <div className="header-gradient">
            <div className="header-content">
              <div className="header-text">
                <h1>Sipariş Detayı</h1>
                <p className="order-id">#{order.orderId}</p>
              </div>
            </div>
            <div
              className="status-badge-large"
              style={{ backgroundColor: getStatusColor(order.status) }}
            >
              {getStatusLabel(order.status)}
            </div>
          </div>
        </div>

        {/* Main Content Grid */}
        <div className="qr-detail-content">
          {/* Firma Card */}
          <div className="info-card gradient-card-1">
            <div className="card-content">
              <h3>Firma Bilgileri</h3>
              <div className="info-grid">
                <div className="info-item">
                  <span className="info-label">Firma Adı:</span>
                  <span className="info-value">
                    {order.firm?.firmName || "-"}
                  </span>
                </div>
                <div className="info-item">
                  <span className="info-label">Firma Kodu:</span>
                  <span className="info-value">
                    {order.firm?.firmCode || "-"}
                  </span>
                </div>
                {order.firm?.contactPerson && (
                  <div className="info-item">
                    <span className="info-label">Yetkili:</span>
                    <span className="info-value">
                      {order.firm.contactPerson}
                    </span>
                  </div>
                )}
                {order.firm?.phone && (
                  <div className="info-item">
                    <span className="info-label">Telefon:</span>
                    <span className="info-value">{order.firm.phone}</span>
                  </div>
                )}
                {order.firm?.email && (
                  <div className="info-item">
                    <span className="info-label">E-posta:</span>
                    <span className="info-value">{order.firm.email}</span>
                  </div>
                )}
              </div>
            </div>
          </div>

          {/* Model Card */}
          <div className="info-card gradient-card-6">
            <div className="card-content">
              <h3>Model Bilgileri</h3>
              <div className="info-grid">
                <div className="info-item">
                  <span className="info-label">Model Adı:</span>
                  <span className="info-value">
                    {order.model?.modelName || "-"}
                  </span>
                </div>
                <div className="info-item">
                  <span className="info-label">Model Kodu:</span>
                  <span className="info-value">
                    {order.model?.modelCode || "-"}
                  </span>
                </div>
                {order.model?.category && (
                  <div className="info-item">
                    <span className="info-label">Kategori:</span>
                    <span className="info-value">{order.model.category}</span>
                  </div>
                )}
                {order.model?.season && (
                  <div className="info-item">
                    <span className="info-label">Sezon:</span>
                    <span className="info-value">{order.model.season}</span>
                  </div>
                )}
                {order.model?.color && (
                  <div className="info-item">
                    <span className="info-label">Renk:</span>
                    <span className="info-value">{order.model.color}</span>
                  </div>
                )}
                {order.model?.size && (
                  <div className="info-item">
                    <span className="info-label">Beden:</span>
                    <span className="info-value">{order.model.size}</span>
                  </div>
                )}
                {order.model?.fabric && (
                  <div className="info-item">
                    <span className="info-label">Kumaş:</span>
                    <span className="info-value">{order.model.fabric}</span>
                  </div>
                )}
              </div>
              {order.model?.description && (
                <div className="info-item" style={{ marginTop: "15px" }}>
                  <span className="info-label">Açıklama:</span>
                  <p className="note-text" style={{ marginTop: "5px" }}>
                    {order.model.description}
                  </p>
                </div>
              )}
            </div>
          </div>

          {/* Miktar & Fiyat Card */}
          <div className="info-card gradient-card-2">
            <div className="card-content">
              <h3>Sipariş Bilgileri</h3>
              <div className="info-grid">
                <div className="info-item">
                  <span className="info-label">Miktar:</span>
                  {!isEditingQuantity ? (
                    <div
                      style={{
                        display: "flex",
                        alignItems: "center",
                        gap: "10px",
                      }}
                    >
                      <span className="info-value highlight">
                        {order.quantity} {getUnitLabel(order.unit)}
                      </span>
                      <button
                        className="btn-inline-edit"
                        onClick={() => setIsEditingQuantity(true)}
                        title="Adedi düzenle"
                        style={{
                          padding: "4px 8px",
                          fontSize: "12px",
                          cursor: "pointer",
                          border: "1px solid #3498db",
                          borderRadius: "4px",
                          backgroundColor: "white",
                          color: "#3498db",
                        }}
                      >
                        ✏️ Düzenle
                      </button>
                    </div>
                  ) : (
                    <div
                      style={{
                        display: "flex",
                        alignItems: "center",
                        gap: "8px",
                      }}
                    >
                      <input
                        type="number"
                        min="1"
                        value={quantityValue}
                        onChange={(e) =>
                          setQuantityValue(parseInt(e.target.value) || 0)
                        }
                        disabled={savingQuantity}
                        autoFocus
                        style={{
                          width: "100px",
                          padding: "6px",
                          border: "2px solid #3498db",
                          borderRadius: "4px",
                          fontSize: "14px",
                        }}
                      />
                      <button
                        onClick={handleSaveQuantity}
                        disabled={savingQuantity}
                        style={{
                          padding: "6px 12px",
                          backgroundColor: "#28a745",
                          color: "white",
                          border: "none",
                          borderRadius: "4px",
                          cursor: savingQuantity ? "not-allowed" : "pointer",
                          fontSize: "12px",
                        }}
                      >
                        {savingQuantity ? "Kaydediliyor..." : "✓ Kaydet"}
                      </button>
                      <button
                        onClick={handleCancelQuantity}
                        disabled={savingQuantity}
                        style={{
                          padding: "6px 12px",
                          backgroundColor: "#dc3545",
                          color: "white",
                          border: "none",
                          borderRadius: "4px",
                          cursor: savingQuantity ? "not-allowed" : "pointer",
                          fontSize: "12px",
                        }}
                      >
                        ✕ İptal
                      </button>
                    </div>
                  )}
                </div>
                {order.pieceCount && (
                  <div className="info-item">
                    <span className="info-label">Parça/Takım:</span>
                    <span className="info-value">{order.pieceCount}</span>
                  </div>
                )}
                {user?.role === "Manager" && order.price && (
                  <div className="info-item">
                    <span className="info-label">Fiyat:</span>
                    <span className="info-value highlight">
                      {formatCurrency(order.price, order.priceCurrency)}
                    </span>
                  </div>
                )}
                {user?.role === "Manager" && order.price && order.quantity && (
                  <div className="info-item">
                    <span className="info-label">Toplam:</span>
                    <span className="info-value highlight-total">
                      {formatCurrency(
                        order.price * order.quantity,
                        order.priceCurrency,
                      )}
                    </span>
                  </div>
                )}
              </div>
            </div>
          </div>

          {/* Atölye Card with Transfer Button */}
          <div className="info-card gradient-card-3">
            <div className="card-content">
              <h3>Atölye Bilgileri</h3>
              <div className="workshop-info">
                <div className="current-workshop">
                  <span className="workshop-label">Mevcut Atölye:</span>
                  <span className="workshop-name">
                    {order.workshop?.name || "Atanmadı"}
                  </span>
                </div>

                {/* Workshop Selection */}
                <div className="workshop-select-wrapper">
                  <label className="workshop-select-label">
                    Yeni Atölye Seç:
                  </label>
                  <select
                    className="workshop-select"
                    value={selectedWorkshopId}
                    onChange={(e) => setSelectedWorkshopId(e.target.value)}
                  >
                    <option value="">Atölye Seçiniz</option>
                    {workshops
                      .filter((w) => w.workshopId !== order.workshopId)
                      .map((w) => (
                        <option key={w.workshopId} value={w.workshopId}>
                          {w.name}
                        </option>
                      ))}
                  </select>
                </div>

                <button
                  className="transfer-button"
                  onClick={() => {
                    if (selectedWorkshopId) {
                      setShowTransferModal(true);
                    } else {
                      setAlertModal({
                        isOpen: true,
                        message: "Lütfen önce yeni bir atölye seçiniz!",
                        type: "warning",
                      });
                    }
                  }}
                  disabled={
                    order.status === OrderStatus.Tamamlandi ||
                    !selectedWorkshopId
                  }
                >
                  Atölye Transfer Et
                </button>
              </div>
              {order.operator && (
                <div className="info-item" style={{ marginTop: "15px" }}>
                  <span className="info-label">Operatör:</span>
                  <span className="info-value">
                    {`${order.operator.firstName} ${order.operator.lastName}`}
                  </span>
                </div>
              )}
            </div>
          </div>

          {/* Tarihler Card */}
          <div className="info-card gradient-card-4">
            <div className="card-content">
              <h3>Tarihler & Öncelik</h3>
              <div className="info-grid">
                <div className="info-item">
                  <span className="info-label">Kabul Tarihi:</span>
                  <span className="info-value">
                    {formatDate(order.acceptanceDate)}
                  </span>
                </div>
                {order.deadline && (
                  <div className="info-item">
                    <span className="info-label">Termin:</span>
                    <span className="info-value">
                      {formatDate(order.deadline)}
                    </span>
                  </div>
                )}
                {order.completionDate && (
                  <div className="info-item">
                    <span className="info-label">Tamamlanma:</span>
                    <span className="info-value">
                      {formatDate(order.completionDate)}
                    </span>
                  </div>
                )}
                {order.priority && (
                  <div className="info-item">
                    <span className="info-label">Öncelik:</span>
                    <span className="info-value highlight">
                      {order.priority}
                    </span>
                  </div>
                )}
              </div>
            </div>
          </div>

          {/* Not Card */}
          <div className="info-card full-width gradient-card-6">
            <div className="card-content">
              <div className="note-section">
                <div className="note-header">
                  <h3>Sipariş Notu</h3>
                  {!isEditingNote && (
                    <button
                      className="btn-note-edit"
                      onClick={() => setIsEditingNote(true)}
                      title={order.note ? "Notu düzenle" : "Not ekle"}
                    >
                      ✏️ {order.note ? "Düzenle" : "Not Ekle"}
                    </button>
                  )}
                </div>

                {!isEditingNote ? (
                  <div className="note-content">
                    {order.note ? (
                      <p className="note-text">{order.note}</p>
                    ) : (
                      <p className="note-placeholder">
                        Henüz not eklenmemiş. Not eklemek için yukarıdaki butona
                        tıklayın.
                      </p>
                    )}
                  </div>
                ) : (
                  <div className="note-edit-section">
                    <textarea
                      className="note-textarea"
                      value={noteText}
                      onChange={(e) => setNoteText(e.target.value)}
                      placeholder="Not giriniz..."
                      disabled={savingNote}
                      autoFocus
                    />
                    <div className="note-actions">
                      <button
                        className="btn-note-cancel"
                        onClick={handleCancelNote}
                        disabled={savingNote}
                      >
                        İptal
                      </button>
                      <button
                        className="btn-note-save"
                        onClick={handleSaveNote}
                        disabled={savingNote}
                      >
                        {savingNote ? "Kaydediliyor..." : "Kaydet"}
                      </button>
                    </div>
                  </div>
                )}
              </div>
            </div>
          </div>

          {/* Teknikler Card */}
          {order.orderTechnics && order.orderTechnics.length > 0 && (
            <div className="info-card full-width gradient-card-5">
              <div className="card-content">
                <h3>Teknikler</h3>
                <div className="technics-grid">
                  {order.orderTechnics.map((ot, index) => (
                    <div
                      key={ot.orderTechnicId || index}
                      className="technic-badge"
                    >
                      {ot.technic?.name || "-"}
                    </div>
                  ))}
                </div>
              </div>
            </div>
          )}

          {/* Fatura Bilgileri */}
          {(order.invoice || order.invoiceNumber) && (
            <div className="info-card full-width">
              <div className="card-content">
                <h3>Fatura Bilgileri</h3>
                <div className="info-grid">
                  {order.invoice && (
                    <div className="info-item">
                      <span className="info-label">Fatura:</span>
                      <span className="info-value">{order.invoice}</span>
                    </div>
                  )}
                  {order.invoiceNumber && (
                    <div className="info-item">
                      <span className="info-label">Fatura No:</span>
                      <span className="info-value">{order.invoiceNumber}</span>
                    </div>
                  )}
                </div>
              </div>
            </div>
          )}

          {/* Görseller */}
          {((order.images && order.images.length > 0) ||
            order.model?.imageUrl) && (
            <div className="info-card full-width">
              <div className="card-content">
                <h3>Görseller</h3>
                <div className="images-grid-qr">
                  {/* Model Görseli */}
                  {order.model?.imageUrl && (
                    <div
                      className="image-item-qr model-image"
                      onClick={() => setLightboxImage(order.model!.imageUrl!)}
                    >
                      <img
                        src={order.model.imageUrl}
                        alt={order.model.modelName || "Model"}
                      />
                      <p>Model Görseli</p>
                    </div>
                  )}
                  {/* Sipariş Görselleri */}
                  {(order.images ?? []).map((img) => {
                    const imageUrl = getImageUrl(img.imageUrl);
                    return (
                      <div
                        key={img.orderImageId}
                        className="image-item-qr"
                        onClick={() => setLightboxImage(imageUrl)}
                      >
                        <img src={imageUrl} alt={img.description || "Order"} />
                        {img.description && <p>{img.description}</p>}
                      </div>
                    );
                  })}
                </div>
              </div>
            </div>
          )}
        </div>

        {/* Lightbox for images */}
        {lightboxImage && (
          <div
            className="lightbox-overlay"
            onClick={() => setLightboxImage(null)}
          >
            <div className="lightbox-content">
              <button
                className="lightbox-close"
                onClick={() => setLightboxImage(null)}
              >
                ✕
              </button>
              <img src={lightboxImage} alt="Full size" />
            </div>
          </div>
        )}

        {/* Footer Info */}
        <div className="qr-detail-footer">
          <div className="footer-info">
            <span>Oluşturulma: {formatDate(order.createdAt)}</span>
            {order.updatedAt && (
              <span>Güncelleme: {formatDate(order.updatedAt)}</span>
            )}
          </div>
        </div>

        {/* Transfer Modal */}
        {showTransferModal && order && selectedWorkshopId && (
          <WorkshopTransferModal
            isOpen={showTransferModal}
            orderId={order.orderId}
            orderQuantity={order.quantity}
            oldWorkshopId={order.workshopId || null}
            oldWorkshopName={order.workshop?.name || "Atanmadı"}
            newWorkshopId={selectedWorkshopId}
            newWorkshopName={
              workshops.find((w) => w.workshopId === selectedWorkshopId)
                ?.name || "Yeni Atölye"
            }
            currentUser={user || undefined}
            onClose={() => {
              setShowTransferModal(false);
              setSelectedWorkshopId("");
              setSelectedUserId("");
            }}
            onSave={handleTransferSave}
          />
        )}

        {/* Alert Modal */}
        <AlertModal
          isOpen={alertModal.isOpen}
          message={alertModal.message}
          type={alertModal.type}
          onClose={() => setAlertModal({ ...alertModal, isOpen: false })}
        />
      </div>
    </>
  );
};

export default OrderQRDetail;
